import { EntityLinks, Goal, ID } from '@griply/types';

import { getGoalParentIds } from './goals/get-goal-parents';

export type DerivedGoalStateOptions = {
  links?: EntityLinks;
};

type FlagResolution = {
  goalsById: Map<ID, Goal>;
  hasFlag: (goal: Goal) => boolean;
  links?: EntityLinks;
  resolved: Map<ID, boolean>;
};

// A goal inherits a flag only when EVERY parent carries it — one live parent keeps the child
// live, the rule a task linked to several goals already follows. Parents already on the path
// are dropped rather than assumed: a cycle must not let a goal prove its own archived state.
const resolveInheritedFlag = (
  goal: Goal,
  { goalsById, hasFlag, links, resolved }: FlagResolution,
  visited: Set<ID>,
): boolean => {
  const cached = resolved.get(goal.id);
  if (cached !== undefined) {
    return cached;
  }

  if (hasFlag(goal)) {
    resolved.set(goal.id, true);

    return true;
  }

  const parents = getGoalParentIds(goal, { links })
    .filter((parentId) => !visited.has(parentId))
    .map((parentId) => goalsById.get(parentId))
    .filter((parent): parent is Goal => !!parent);

  const inherited =
    !!parents.length &&
    parents.every((parent) =>
      resolveInheritedFlag(
        parent,
        { goalsById, hasFlag, links, resolved },
        new Set<ID>([...visited, parent.id]),
      ),
    );

  resolved.set(goal.id, inherited);

  return inherited;
};

// Parents are looked up inside `goals` — pass the full set; a filtered subset breaks the walk and
// orphans resolve as unflagged.
const collectGoalIdsWithInheritedFlag = (
  goals: Goal[],
  hasFlag: (goal: Goal) => boolean,
  { links }: DerivedGoalStateOptions = {},
): Set<ID> => {
  const resolution: FlagResolution = {
    goalsById: new Map(goals.map((goal) => [goal.id, goal])),
    hasFlag,
    links,
    resolved: new Map<ID, boolean>(),
  };

  goals.forEach((goal) =>
    resolveInheritedFlag(goal, resolution, new Set<ID>([goal.id])),
  );

  const flaggedIds = new Set<ID>();
  resolution.resolved.forEach((isFlagged, id) => {
    if (isFlagged) {
      flaggedIds.add(id);
    }
  });

  return flaggedIds;
};

export const isGoalArchived = (
  goal: Goal,
  goals: Goal[],
  { links }: DerivedGoalStateOptions = {},
): boolean =>
  resolveInheritedFlag(
    goal,
    {
      goalsById: new Map(goals.map((entry) => [entry.id, entry])),
      hasFlag: ({ archivedAt }) => !!archivedAt,
      links,
      resolved: new Map<ID, boolean>(),
    },
    new Set<ID>([goal.id]),
  );

export const getArchivedGoalIds = (
  goals: Goal[],
  options?: DerivedGoalStateOptions,
): Set<ID> =>
  collectGoalIdsWithInheritedFlag(goals, (goal) => !!goal.archivedAt, options);

// Visibility only — a goal under a completed branch is still unfinished work, not completed.
export const getCompletedBranchGoalIds = (
  goals: Goal[],
  options?: DerivedGoalStateOptions,
): Set<ID> =>
  collectGoalIdsWithInheritedFlag(goals, (goal) => !!goal.completedAt, options);
